<template>
  <div class="scene" ref="scene">
    <canvas ref="canvas"></canvas>
    <div class="view-buttons">
      <div
        class="view-button"
        v-bind:class="viewAngleIcon"
        v-on:click="nextViewAngle()">
      </div>
      <div
        class="rotate-button icon-rotate-right"
        v-on:mousedown="startRotate(-0.01)"
        v-on:mouseup="stopRotate"
        v-on:mouseout="stopRotate">
      </div>
    </div>
  </div>
</template>

<script>

import * as THREE from 'THREE';
import THREEService from 'services/three-js-service';

require('imports-loader?THREE=THREE!lib/OrbitControls');

const IsOrthographic = true;
const IsShowBoundingBox = false;
// required to be true when using canvas.toData
const IsPreserveDrawingBuffer = false;
let orthographicCameraSize = 1.1;

const defaultTheta = THREEService.degreeToRadian(30);
const defaultPhi = THREEService.degreeToRadian(75);

export default {
  name: 'scene',
  data() {
    return {
      renderer: null,
      camera: null,
      controls: null,
      isRotating: false,
      curViewAngleIndex: 0,
      _boundingBox: null
    };
  },
  props: ['product'],
  watch: {
    product(value, oldValue) {
      if (oldValue) {
        THREEService.curScene.remove(oldValue.model);
      }
      if (value) {
        THREEService.curScene.add(value.model);
      }
    },
    curViewAngleIndex(value) {
      switch (value) {
        case 0:
          this.changeViewAngle(30);
          break;
        case 1:
          this.changeViewAngle(-30);
          break;
        case 2:
          this.changeViewAngle(-150);
          break;
        case 3:
          this.changeViewAngle(150);
          break;
        default:
          this.changeViewAngle(30);
          break;
      }
    }
  },
  mounted() {
    if (IsShowBoundingBox) {
      this.$watch('product.dimensions', this.updateBoundingBox);
    }

    this.initRenderer(this.$refs.canvas);
    this.initCamera(IsOrthographic);
    this.initControls();
    this.initLights();

    this.controls.setTheta(defaultTheta);
    this.controls.setPhi(defaultPhi);

    this.updateSize();
    window.addEventListener('resize', this.updateSize, false);
    this.$refs.canvas.addEventListener('wheel', (e) => {
      orthographicCameraSize = Math.max(1,
        Math.min(2, orthographicCameraSize + (e.deltaY / 1000)));
      this.updateSize();
    });

    if (this.product) {
      THREEService.curScene.add(this.product.model);
    }

    this.animate();
  },
  computed: {
    viewAngleIcon() {
      switch (this.curViewAngleIndex) {
        case 0:
          return 'icon-front-chair';
        case 1:
          return 'icon-front-chair flip';
        case 2:
          return 'icon-back-chair flip';
        case 3:
          return 'icon-back-chair';
        default:
          return 'icon-front-chair';
      }
    }
  },
  methods: {
    initRenderer(canvas) {
      const pixelRatio = window.devicePixelRatio || 1;

      this.renderer = new THREE.WebGLRenderer({
        preserveDrawingBuffer: IsPreserveDrawingBuffer,
        canvas,
        antialias: pixelRatio === 1
      });

      this.renderer.setPixelRatio(pixelRatio);
      this.renderer.setClearColor(new THREE.Color('rgb(242, 242, 242)'), 1);
    },
    initCamera(useOrthographic) {
      if (useOrthographic) {
        this.camera = new THREE.OrthographicCamera(0, 0, 0, 0, 1, 100000);
      } else {
        this.camera = new THREE.PerspectiveCamera(45, 1, 1, 100000);
      }
      this.camera.position.z = 1000;
    },
    initControls() {
      this.controls = new THREE.OrbitControls(this.camera,
        this.renderer.domElement);
      this.controls.noZoom = IsOrthographic;
      this.controls.noPan = true;
      this.controls.noRoll = true;
      this.controls.maxPolarAngle = Math.PI / 2;
    },
    initLights() {
      const ambientLight = new THREE.AmbientLight(0x404040); // soft white light
      const directionalKeyLight = new THREE.DirectionalLight(0xaaaaaa, 1);
      const directionalFillLight = new THREE.DirectionalLight(0xaaaaaa, 0.5);
      const directionalBackLight = new THREE.DirectionalLight(0xaaaaaa, 0.5);

      directionalKeyLight.position.set(-20, 20, 20).normalize();
      directionalFillLight.position.set(10, 10, 20).normalize();
      directionalBackLight.position.set(0, 20, -50).normalize();

      THREEService.curScene.add(ambientLight);
      THREEService.curScene.add(directionalKeyLight);
      THREEService.curScene.add(directionalFillLight);
      THREEService.curScene.add(directionalBackLight);
    },
    updateCameraParams(width, height) {
      if (this.camera.isOrthographicCamera) {
        this.camera.left = -width / orthographicCameraSize;
        this.camera.right = width / orthographicCameraSize;
        this.camera.top = (height / orthographicCameraSize) + 50;
        this.camera.bottom = (-height / orthographicCameraSize) + 50;
      } else {
        this.camera.aspect = width / height;
      }

      this.camera.updateProjectionMatrix();
    },
    render() {
      this.renderer.render(THREEService.curScene, this.camera);
    },
    rotate() {
      if (this.rotateAngle) {
        this.controls.setTheta(this.controls.getTheta() + this.rotateAngle);
      }
    },
    animate() {
      this.render();
      this.rotate();
      requestAnimationFrame(this.animate);
      this.controls.update();
    },
    updateSize() {
      const style = window.getComputedStyle(this.$refs.scene);
      const newWidth = parseFloat(style.width);
      const newHeight = parseFloat(style.height);

      this.updateCameraParams(newWidth, newHeight);
      this.renderer.setSize(newWidth, newHeight);
      if (this.controls.handleResize) {
        this.controls.handleResize();
      }
    },
    changeViewAngle(angle) {
      this.controls.setPhi(defaultPhi);
      this.controls.setTheta(THREEService.degreeToRadian(angle));
    },
    nextViewAngle() {
      if (this.curViewAngleIndex < 3) {
        this.curViewAngleIndex += 1;
      } else {
        this.curViewAngleIndex = 0;
      }
    },
    startRotate(value) {
      this.rotateAngle = value;
    },
    stopRotate() {
      this.rotateAngle = 0;
    },
    updateBoundingBox(value) {
      if (this._boundingBox) {
        THREEService.curScene.remove(this._boundingBox);
        this._boundingBox = null;
      }

      const boundingBox = new THREE.Box3().setFromObject(this.product.model);
      const size = boundingBox.getSize();
      const center = boundingBox.getCenter();
      const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
      const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });

      material.wireframe = true;
      geometry.translate(center.x, center.y, center.z);

      this._boundingBox = new THREE.Mesh(geometry, material);
      THREEService.curScene.add(this._boundingBox);

      console.log(size.x, size.y, size.z);
      console.log(value.width, value.height, value.depth);
    },
    saveImages() {
      // Output images of the model from all angles.
      return import('services/canvas-saver').then(
        ({ default: CanvasSaver }) => {
          /* eslint-disable */
          let promise = Promise.resolve();
          for (let phi = 0, i = 0; phi <= 90; phi += 9, i += 1) {
            for (let theta = 0, j = 0; theta < 360; theta += 12, j += 1) {
              promise = promise.then(() => {
                return new Promise((resolve) => {
                  this.controls.setTheta(THREEService.degreeToRadian(theta));
                  this.controls.setPhi(THREEService.degreeToRadian(90 - phi));
                  setTimeout(() => {
                    console.log(`${theta}_${phi}`);
                    CanvasSaver.save(this.$refs.canvas, `${i}_${j}`)
                      .then(resolve);
                  }, 200);
                });
              });
            }
          }
          /* eslint-enable */
          return promise;
        });
    }
  }
};

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.scene {
  position: relative;
  width: 100%;
  height: 100%;
}
.view-buttons {
  position: absolute;
  bottom: 10px;
  right: 20px;
}
.view-button {
  cursor: pointer;
  font-size: 80px;
  color: #A9ACA6;
  position: relative;
  left: 2px;
  top: 20px;
}
.rotate-button {
  cursor: pointer;
  font-size: 46px;
  color: #A9ACA6;
  height: 50px;
  position: relative;
  z-index: 10;
}
.view-button:hover,
.rotate-button:hover {
  color: #6DADAD;
}
.view-button.flip,
.rotate-button.flip {
  transform: scaleX(-1);
  -moz-transform: scaleX(-1);
  -webkit-transform: scaleX(-1);
}
</style>



// WEBPACK FOOTER //
// scene.vue?2a501254